/**
 * @file CloseSiteDialog.tsx
 * @description Окно «Закрыть сайт для покупателей?» (phase19.16, SB138;
 * uiux-site-closed.md §3, §8; db-design-site-closed.md §3, §6). Одно окно
 * для раздела «Доступ к сайту» и «⋯» конструктора. До sm — на весь экран с
 * закреплённым низом [Отмена] [Закрыть сайт], с sm — окно 560 px. Главная
 * кнопка — тон «внимание» (янтарь), не красная.
 *
 * Ошибки: нет связи — окно остаётся, «Сайт по-прежнему открыт» и
 * [Повторить]; 409 «уже закрыт» и «закрыт платформой» — окно закрывается,
 * текст сервера («Сайт уже закрыт с 14:18 (Мария).») в toast, состояние
 * перечитывается; ошибки текста и времени — под полями. Успех —
 * toast «Сайт закрыт для покупателей — изменение вступит в силу в течение
 * 10 секунд»; замечания к тексту (не блокируют) — отдельным toast.
 */

import { siteClosureDiscountTerm } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { NoticeStrip } from "../ui/NoticeStrip";
import { useNotify } from "../ui/NotifyProvider";
import { CloseSiteFields } from "./CloseSiteFields";
import { closeSiteRequest, hasCloseSiteErrors, initialCloseSiteForm, validateCloseSite, type CloseSiteErrors, type CloseSiteForm } from "./close-site-model";
import { SITE_ACCESS_DELAY_NOTE } from "../../hooks/site-access/useSiteAccessActions";
import type { SiteAccessHandle } from "../../hooks/site-access/useSiteAccess";
import { ApiError } from "../../lib/api-client";
import { siteAccess } from "../../lib/api-site-access";
import { isSiteAccessNetworkError, isSiteAccessStale, siteAccessErrorText } from "../../lib/site-access/site-access-errors";
import { safeTimeZone } from "../../lib/site-access/site-access-time";
import { cn } from "@/lib/utils";

/** Что произойдёт после закрытия (uiux §3). */
const CONSEQUENCES = [
  "Покупатели увидят заглушку с вашим текстом, контактами и реквизитами.",
  "Поисковики получат «временно недоступен» — позиции не теряются, если пауза короткая.",
  "Оплаты, письма, заказы в кабинете, «Мой заказ» и документы работают.",
  "Вы и сотрудники видите сайт как обычно — кнопка «Смотреть сайт» в плашке кабинета.",
  "Реклама, рассылки и маркетплейсы не останавливаются — приостановите их сами.",
] as const;

/** Сайт уже закрыт (кем-то ещё или платформой): окно больше не нужно — текст в toast. */
const CLOSED_ELSEWHERE: ReadonlySet<string> = new Set(["site_already_closed", "site_closed_by_platform"]);

/** Почему «Закрыть сайт» неактивна при цифре скидки в тексте. */
export const DISCOUNT_BLOCK_REASON = "Уберите цифры скидки из текста — с ними сайт не закрыть.";

/** Ошибка сервера по полю → куда её показать (реестр). */
const FIELD_ERRORS: Readonly<Record<string, keyof CloseSiteErrors>> = {
  site_closure_text_too_long: "message",
  site_closure_text_discount: "message",
  site_closure_expected_invalid: "expected",
};

interface Props {
  open: boolean;
  onClose: () => void;
  access: SiteAccessHandle;
}

export function CloseSiteDialog({ open, onClose, access }: Props) {
  const notify = useNotify();
  const zone = safeTimeZone(access.state?.timeZone);
  const [form, setForm] = useState<CloseSiteForm>(() => initialCloseSiteForm(access.serverNow(), zone));
  const [errors, setErrors] = useState<CloseSiteErrors>({});
  const [problem, setProblem] = useState<{ text: string; retry: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  // Каждое открытие — с чистой формы и днём «сегодня» по времени магазина.
  useEffect(() => {
    if (!open) return;
    setForm(initialCloseSiteForm(access.serverNow(), zone));
    setErrors({});
    setProblem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только на открытие
  }, [open]);

  // Цифра скидки (SB57) — кнопка выглядит неактивной и говорит почему (QA D1).
  const discountBlocked = siteClosureDiscountTerm(form.message) !== null;

  async function submit() {
    const found = validateCloseSite(form, access.serverNow(), zone);
    setErrors(found);
    if (discountBlocked) notify.warning(DISCOUNT_BLOCK_REASON);
    if (hasCloseSiteErrors(found)) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await siteAccess.close(closeSiteRequest(form, zone));
      access.apply(result.state);
      notify.success(`Сайт закрыт для покупателей — ${SITE_ACCESS_DELAY_NOTE}`);
      if (result.warnings.length > 0) notify.warning(`Проверьте текст заглушки: ${result.warnings.map((w) => w.message).join(" ")}`);
      onClose();
    } catch (err) {
      const field = err instanceof ApiError ? FIELD_ERRORS[err.code] : undefined;
      const text = siteAccessErrorText(err, "close", zone);
      if (isSiteAccessStale(err)) void access.reload();
      if (err instanceof ApiError && CLOSED_ELSEWHERE.has(err.code)) {
        notify.warning(text);
        onClose();
        return;
      }
      if (field) setErrors({ [field]: text });
      else setProblem({ text, retry: isSiteAccessNetworkError(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !busy ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-h-[90vh] sm:max-w-[35rem] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Закрыть сайт для покупателей?</DialogTitle>
          <DialogDescription className="sr-only">Покупатели увидят заглушку, вы и сотрудники продолжите работать.</DialogDescription>
        </DialogHeader>
        <ul className="type-body flex list-disc flex-col gap-1 pl-5 text-on-surface">
          {CONSEQUENCES.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {problem ? (
          <div className="flex flex-col gap-2">
            <NoticeStrip tone="error">{problem.text}</NoticeStrip>
            {problem.retry ? (
              <Button variant="outline" className="self-start" onClick={() => void submit()} disabled={busy}>
                Повторить
              </Button>
            ) : null}
          </div>
        ) : null}
        <CloseSiteFields
          value={form}
          onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          errors={errors}
          now={access.serverNow()}
          zone={zone}
          disabled={busy}
        />
        <DialogFooter className="sticky -bottom-6 z-10 -mx-6 -mb-6 max-sm:mt-auto bg-surface-lowest px-6 py-3 [box-shadow:0_-1px_0_var(--outline-variant)] flex-row justify-end">
          <Button variant="ghost" className="max-sm:flex-1" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button
            variant="warning"
            className={cn("max-sm:flex-1", discountBlocked && "cursor-not-allowed opacity-50")}
            aria-disabled={discountBlocked || undefined}
            aria-describedby={discountBlocked ? "close-site-blocked" : undefined}
            loading={busy}
            onClick={() => void submit()}
          >
            Закрыть сайт
          </Button>
          {discountBlocked ? (
            <span id="close-site-blocked" className="sr-only">
              {DISCOUNT_BLOCK_REASON}
            </span>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
